/**
 * Enums shared by the api and both frontends.
 *
 * These are `as const` objects rather than TypeScript `enum`s so they survive
 * type-erasure-only transpilers (Vite/esbuild, tsx) and stay plain JSON values
 * across the wire.
 *
 * IMPORTANT: every member here mirrors a Prisma enum in
 * `apps/api/prisma/schema.prisma` exactly. The database design is authoritative
 * for storage — do not add a member here without a matching migration.
 */
/** The four TeenTack account roles. [FR-M01.4] */
export const UserRole = {
    TEEN: 'TEEN',
    PARENT: 'PARENT',
    JOB_POSTER: 'JOB_POSTER',
    ADMIN: 'ADMIN',
};
export const USER_ROLES = Object.values(UserRole);
/** Roles that may self-register. Admins are created by a Super-Admin. [FR-M01.1, FR-M01.5] */
export const SELF_REGISTERABLE_ROLES = [
    UserRole.TEEN,
    UserRole.PARENT,
    UserRole.JOB_POSTER,
];
/**
 * Account lifecycle. Mirrors the `user_status` DB enum.
 *
 * Note there is no dedicated "awaiting parent approval" member: a minor Teen
 * stays PENDING until their guardian relationship is verified. Use
 * `isAwaitingParentApproval()` rather than testing status alone. [FR-M01.2]
 */
export const UserStatus = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
    BANNED: 'BANNED',
};
/** Only ACTIVE accounts may use the platform beyond the auth surface. */
export function isUsableStatus(status) {
    return status === UserStatus.ACTIVE;
}
/** Verification lifecycle for Parent / Teen / Job Poster profiles. */
export const VerificationStatus = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
/**
 * Granular Admin permissions, additive and least-privilege by default.
 *
 * Stored as boolean flags in `roles_permissions.permissions_json`, keyed by
 * these values. [FR-M01.5]
 */
export const AdminPermission = {
    MODERATION: 'MODERATION',
    COMPLIANCE: 'COMPLIANCE',
    FINANCE: 'FINANCE',
    SUPPORT: 'SUPPORT',
    SUPER_ADMIN: 'SUPER_ADMIN',
};
export const ADMIN_PERMISSIONS = Object.values(AdminPermission);
/** Channel an OTP is delivered over. Derived from the target, not stored. */
export const OtpChannel = {
    EMAIL: 'EMAIL',
    SMS: 'SMS',
};
/** Mirrors the `otp_purpose` DB enum. LOGIN is the step-up MFA code. [FR-M01.3] */
export const OtpPurpose = {
    SIGNUP: 'SIGNUP',
    LOGIN: 'LOGIN',
    PASSWORD_RESET: 'PASSWORD_RESET',
    PHONE_CHANGE: 'PHONE_CHANGE',
};
/** Placeholder — the jobs module (M05) will own this. */
export const JobStatus = {
    DRAFT: 'DRAFT',
    OPEN: 'OPEN',
    ASSIGNED: 'ASSIGNED',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
};
/** Placeholder — the applications module (M07) will own this. */
export const ApplicationStatus = {
    APPLIED: 'APPLIED',
    SHORTLISTED: 'SHORTLISTED',
    OFFERED: 'OFFERED',
    ACCEPTED: 'ACCEPTED',
    DECLINED: 'DECLINED',
    ASSIGNED: 'ASSIGNED',
    CONFIRMED: 'CONFIRMED',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    CLOSED: 'CLOSED',
};
//# sourceMappingURL=enums.js.map